import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { Fragment, memo, useEffect, useState } from 'react';
import { Button, Menu } from '@simplywallst/ui-core/next';
import { Translation } from '@/hooks/useTranslation';
import useUser from '@/hooks/useUser';
import { useSummary } from '@/hooks/useCompany';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import PlansModal from '@/components/OurPlans/components/PlansModal/PlansModal';
import { MODAL } from './constants';
import { useActions } from './hooks/useActions';
import { useMeasureChildrenWidth } from './hooks/useMeasureChildrenWidth';
import { useOverflowedChildren } from './hooks/useOverflowedChildren';
import { cnLite } from './utils';
import type { Action, ActiveModal } from './types';

const AddRemoveCompanyModal = loadable(
  () => import('@/components/AddRemoveCompany')
);

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal/GuestRegistrationModal'),
  { resolveComponent: (c) => c.GuestRegistrationModal }
);

// Extra padding to account for the menu button gap + early collapse
const BUTTON_EXTRA_WIDTH = 25;

const BUTTON_MORE = 'more';

const ActionButtons = () => {
  const { trackEvent } = useTracking();
  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  const { isSharedCompany } = useCompanyShareLink();
  const { data: user } = useUser();
  const actions = useActions();
  const { widths, measureRef } = useMeasureChildrenWidth();
  const secondLastKey = Object.keys(widths)[Object.keys(widths).length - 2];
  const { items, overflowedItems, containerRef } = useOverflowedChildren({
    ...widths,
    /**
     * The '...' button is always visible so the width is added to the note button instead.
     * This helps hide the note button instead on smaller devices / certain languages.
     */
    [secondLastKey]: Object.values(widths)
      .slice(-2)
      .reduce((a, b) => a + b, BUTTON_EXTRA_WIDTH),
    [BUTTON_MORE]: 0,
  });
  const summary = useSummary();
  const {
    companyId = '',
    uniqueSymbol = '',
    canonicalUrl = '',
  } = summary.status === 'success' ? summary : {};

  // Avoid showing registration modal once user is registered
  useEffect(() => {
    if (user.loggedIn && activeModal === MODAL.REGISTRATION) {
      setActiveModal(MODAL.ADD_REMOVE_COMPANY);
    }
  }, [user.loggedIn, activeModal]);

  const showButtons = user.loggedIn || !isSharedCompany;
  if (!showButtons) return;

  const actionList = {
    primary: actions.filter((a) => {
      // When item has no smallProps, it's always un-collapsed
      if (!a.smallProps) return true;
      return items.includes(a.key);
    }),
    secondary: actions.filter((a) => {
      // When item has no largeProps, it's always collapsed
      if (!a.largeProps) return true;
      return overflowedItems.includes(a.key);
    }),
    primaryAll: actions.filter((a) => a.largeProps),
  };

  const handleSecondaryAction = (action: Action) => {
    action.handleClick({
      isUserLoggedIn: user.loggedIn,
      companyId,
      uniqueSymbol,
      canonicalUrl,
      trackEvent,
      setActiveModal,
    });
  };

  return (
    <>
      <div className="@container/abg">
        <div
          ref={containerRef}
          /**
           * @important
           * Any classes changed here that affect button widths should also be
           * added to the invisible content box below.
           * That content box is used for measuring which items are shown and
           * which are collapsed into the menu button.
           */
          className={cnLite(
            'grid select-none justify-start gap-2',
            // 0 - 339px
            'max-[339px]:grid-rows-2 max-[339px]:grid-cols-7 max-[339px]:[&>*:first-child]:col-span-full max-[339px]:[&>*:nth-child(2)]:col-span-6',
            // 340px+
            'min-[340px]:grid-cols-[50%_1fr_auto] min-[340px]:grid-flow-col',
            // 400px+
            'min-[400px]:grid-cols-none',
            // TODO: Roll these button overrides into the next/Button component.
            '[&_button:disabled]:cursor-not-allowed [&_button]:h-x4 [&_button]:gap-px [&_button]:whitespace-nowrap [&_button_svg]:h-5 [&_button_svg]:w-auto'
          )}
        >
          {actionList.primary.map((a) => (
            <Button
              size="large"
              variant={a.largeProps?.variant ?? 'outline'}
              key={a.key}
              data-key={a.key}
              isDisabled={a.isDisabled}
              className={cnLite(
                'animate-fade-in [animation-fill-mode:backwards] transition-[padding,opacity]',
                a.largeProps?.className
              )}
              onClick={() => {
                a.handleClick({
                  isUserLoggedIn: user.loggedIn,
                  companyId,
                  uniqueSymbol,
                  canonicalUrl,
                  trackEvent,
                  setActiveModal,
                });
              }}
            >
              {a.largeProps?.label}
            </Button>
          ))}

          {actionList.secondary.length > 0 && (
            <Menu
              label={
                <Button
                  size="large"
                  variant="outline"
                  data-key={BUTTON_MORE}
                  className="max-w-fit"
                >
                  <span className="sr-only">
                    {/* TODO: i18n */}
                    More actions
                  </span>
                  <span
                    className="-mt-2.5 text-xl font-bold"
                    aria-hidden="true"
                  >
                    &hellip;
                  </span>
                </Button>
              }
            >
              {actionList.secondary.map((a) => (
                <Menu.Item
                  key={a.key}
                  onAction={() => handleSecondaryAction(a)}
                  textValue={a.smallProps?.label}
                >
                  <div className="grid grid-cols-[1.5em_1fr] items-center gap-2 pr-3 text-xs">
                    {a.smallProps?.icon && (
                      <span
                        className="block size-6 fill-softer"
                        role="presentation"
                      >
                        {a.smallProps.icon}
                      </span>
                    )}
                    {a.smallProps?.label}
                  </div>
                </Menu.Item>
              ))}
            </Menu>
          )}
        </div>

        {/**
         * Invisible content used for measuring widths and responsiveness.
         * It activates at sm: to avoid measuring the buttons on small devices.
         * Preview by removing the 'invisible' and 'indent-[-9999px]' class.
         */}
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute indent-[-9999px] [&_button]:h-x4 [&_button]:gap-px [&_button]:whitespace-nowrap [&_button_svg]:h-5 [&_button_svg]:w-auto"
        >
          {actionList.primaryAll.map((a) => (
            <Button
              size="large"
              variant="outline"
              key={a.key}
              data-measure={a.key}
            >
              {a.largeProps?.label}
            </Button>
          ))}
          <Button size="large" variant="outline" data-measure={BUTTON_MORE}>
            <span className="text-xl font-bold">&hellip;</span>
          </Button>
        </div>
      </div>

      {/**
       * Non-shared modals - Only used for Watchlist at this time.
       */}
      {actions.map((a) => {
        if (!('modals' in a && a.modals)) return;
        return <Fragment key={a.key}>{a.modals}</Fragment>;
      })}

      {/**
       * Shared modals
       */}
      {activeModal === MODAL.PLANS && (
        <PlansModal onClose={() => setActiveModal(MODAL.NONE)}>
          <Translation i18nKey="page.companyReport.header.warning.downloadUnavailable">
            <p>Download Company Report is only available on Unlimited Plan.</p>
            <h2>Upgrade to unlock Company Report Download</h2>
          </Translation>
        </PlansModal>
      )}
      {activeModal === MODAL.ADD_REMOVE_COMPANY &&
        summary.status === 'success' && (
          <AddRemoveCompanyModal
            onClose={() => setActiveModal(MODAL.NONE)}
            tickerSymbol={summary.tickerSymbol}
            companyId={summary.companyId}
            uniqueSymbol={summary.uniqueSymbol}
          />
        )}
      {activeModal === MODAL.REGISTRATION && !user.loggedIn && (
        <GuestRegistrationModal
          canClose
          removeWindowScroll
          onClose={() => setActiveModal(MODAL.NONE)}
          onRegisterSuccess={() => {
            trackEvent({
              type: 'track',
              // TODO: Only "Add to Portfolio" currently calls this so the subject
              // is hardcoded, we'll need to make the tracking more dynamic when
              // we hoist the watchlist modals up to this component.
              // @see https://simplywallst.atlassian.net/browse/RSRCH-2712
              subject: 'addToPortfolio',
              modifier: 'registration',
              action: 'success',
            });
          }}
        />
      )}
    </>
  );
};

export const ActionButtonGroup = memo(ActionButtons);
